/**
 * Mutaciones local-first de HORARIO y ASISTENCIA (F3-A).
 *
 * Escriben en PowerSync → write-path (`db/sync.rs`), que aplica la authz
 * (manager de la cohorte: ManageAcademics con scoping de sede o profesor lead;
 * la asistencia la registra un profesor de la cohorte) y la validación DURA de
 * conflictos de horario. Los denormalizados organization_id/campus_id son
 * autoritativos del servidor (aquí se rellenan para el optimismo local).
 *
 * NO local-first (van por REST, no aquí): generar sesiones en bloque, check-in
 * del alumno, abrir/cerrar código, reporte y acknowledge de alertas.
 */

import {
  attendance_record,
  class_session,
  eq,
  type PowerSyncDrizzleDB,
  schedule_slot,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateUUID } from "../../utils/uuid";

export interface SlotContext {
  cohortId: string;
  organizationId: string;
  campusId?: string | null;
}

// ─── Franjas de horario ──────────────────────────────────────────────────────

export async function createSlotMutation(
  db: PowerSyncDrizzleDB,
  ctx: SlotContext,
  params: {
    weekday: number;
    startTime: string; // HH:MM
    endTime: string;
    roomId?: string | null;
    teacherId?: string | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(schedule_slot).values({
    id,
    cohort_id: ctx.cohortId,
    organization_id: ctx.organizationId,
    campus_id: ctx.campusId ?? null,
    room_id: params.roomId ?? null,
    weekday: params.weekday,
    start_time: normalizeTime(params.startTime),
    end_time: normalizeTime(params.endTime),
    teacher_id: params.teacherId ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateSlotMutation(
  db: PowerSyncDrizzleDB,
  slotId: string,
  updates: Partial<{
    weekday: number;
    start_time: string;
    end_time: string;
    room_id: string | null;
    teacher_id: string | null;
  }>,
): Promise<void> {
  const patch: Record<string, unknown> = { ...updates, updated_at: nowISO() };
  if (typeof updates.start_time === "string") {
    patch.start_time = normalizeTime(updates.start_time);
  }
  if (typeof updates.end_time === "string") {
    patch.end_time = normalizeTime(updates.end_time);
  }
  await db.update(schedule_slot).set(patch).where(eq(schedule_slot.id, slotId));
}

export async function deleteSlotMutation(db: PowerSyncDrizzleDB, slotId: string): Promise<void> {
  const now = nowISO();
  await db
    .update(schedule_slot)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(schedule_slot.id, slotId));
}

// ─── Sesiones de clase ───────────────────────────────────────────────────────

export async function createSessionMutation(
  db: PowerSyncDrizzleDB,
  ctx: SlotContext,
  params: {
    date: string; // YYYY-MM-DD
    startTime: string;
    endTime: string;
    roomId?: string | null;
    teacherId?: string | null;
    topic?: string | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(class_session).values({
    id,
    cohort_id: ctx.cohortId,
    organization_id: ctx.organizationId,
    campus_id: ctx.campusId ?? null,
    room_id: params.roomId ?? null,
    teacher_id: params.teacherId ?? null,
    date: params.date,
    start_time: normalizeTime(params.startTime),
    end_time: normalizeTime(params.endTime),
    status: "scheduled",
    topic: params.topic ?? null,
    checkin_open: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateSessionMutation(
  db: PowerSyncDrizzleDB,
  sessionId: string,
  updates: Partial<{
    date: string;
    start_time: string;
    end_time: string;
    room_id: string | null;
    teacher_id: string | null;
    topic: string | null;
    notes: string | null;
    status: string;
  }>,
): Promise<void> {
  const patch: Record<string, unknown> = { ...updates, updated_at: nowISO() };
  if (typeof updates.start_time === "string") {
    patch.start_time = normalizeTime(updates.start_time);
  }
  if (typeof updates.end_time === "string") {
    patch.end_time = normalizeTime(updates.end_time);
  }
  await db.update(class_session).set(patch).where(eq(class_session.id, sessionId));
}

export async function deleteSessionMutation(
  db: PowerSyncDrizzleDB,
  sessionId: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(class_session)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(class_session.id, sessionId));
}

// ─── Asistencia (pase de lista del profesor, teacher_roll) ───────────────────

export type AttendanceStatus = "present" | "late" | "absent" | "excused";

/**
 * Registra/actualiza la asistencia de un alumno a una sesión. El write-path
 * fuerza method='teacher_roll', recorded_by=actor y recomputa stats/alertas.
 * `existingId` = fila previa a actualizar; ausente = alta nueva.
 */
export async function setAttendanceMutation(
  db: PowerSyncDrizzleDB,
  ctx: { sessionId: string; cohortId: string; organizationId: string; campusId?: string | null },
  params: { userId: string; status: AttendanceStatus; note?: string | null; existingId?: string },
): Promise<string> {
  const now = nowISO();
  if (params.existingId) {
    await db
      .update(attendance_record)
      .set({ status: params.status, note: params.note ?? null, updated_at: now })
      .where(eq(attendance_record.id, params.existingId));
    return params.existingId;
  }
  const id = generateUUID();
  await db.insert(attendance_record).values({
    id,
    class_session_id: ctx.sessionId,
    cohort_id: ctx.cohortId,
    organization_id: ctx.organizationId,
    campus_id: ctx.campusId ?? null,
    user_id: params.userId,
    status: params.status,
    method: "teacher_roll",
    recorded_at: now,
    note: params.note ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Normaliza `HH:MM` → `HH:MM:SS` (las columnas time del server esperan segundos). */
function normalizeTime(t: string): string {
  const trimmed = t.trim();
  return /^\d{2}:\d{2}$/.test(trimmed) ? `${trimmed}:00` : trimmed;
}
